import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Cpu, GitCompareArrows, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useCategories } from "@/hooks/useMeta";
import { ListingCard } from "@/components/common/Cards";
import { Loading, Empty } from "@/components/common/States";
import SEO from "@/components/common/SEO";

const FEATURES = [
  { icon: Cpu, title: "Uyumlu drone topla", text: "ESC'den alıcıya 7 adımda, teknik verilere göre uyumluluk kontrolü.", to: "/drone-topla" },
  { icon: GitCompareArrows, title: "Teknik karşılaştırma", text: "Katalog modellerini ve ilanları yan yana, bilinmeyen değerler açıkça işaretli.", to: "/karsilastir" },
  { icon: ShieldCheck, title: "Katalog tabanlı ilan", text: "Modeli seç, teknik özellikler otomatik dolsun; sadece farkları düzenle.", to: "/ilan-ver" },
];

export default function Home() {
  const { data: cats = [] } = useCategories();
  const { data, isLoading } = useQuery({ queryKey: ["listings", "home"], queryFn: async () => (await api.get("/listings?limit=6")).data });
  return (
    <>
      <SEO
        title="FPV Parça Pazaryeri ve Drone Toplama"
        description="Türkiye'nin FPV pazaryeri: İkinci el ve sıfır FPV parçaları, teknik özellik karşılaştırma ve 7 adımda uyumlu drone toplama sihirbazı."
        canonical="https://createfpv.com/"
      />
      <section className="overflow-hidden bg-white">
        <div className="mx-auto max-w-5xl px-4 pt-16 text-center sm:px-6 sm:pt-24">
          <p className="text-[17px] font-semibold text-[#0071e3]">FPV parça pazaryeri</p>
          <h1 className="mx-auto mt-3 max-w-4xl font-display text-5xl font-semibold leading-[1.05] tracking-tight text-zinc-900 sm:text-6xl lg:text-7xl">
            Parçayı bul.<br className="hidden sm:block" /> Drone'unu topla.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-zinc-500 sm:text-xl">Katalog tabanlı ilanlar, teknik karşılaştırma ve her parçada uyumluluk kontrolü.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="h-11 px-6 text-[15px] bg-[#0071e3] hover:bg-[#0077ed] text-white" data-testid="hero-browse-button"><Link to="/ilanlar">İlanlara göz at</Link></Button>
            <Button asChild size="lg" variant="outline" className="h-11 px-6 text-[15px] border-[#0071e3] text-[#0071e3] bg-transparent hover:bg-[#0071e3]/5" data-testid="hero-builder-button"><Link to="/drone-topla">Drone topla <ArrowRight className="h-4 w-4" /></Link></Button>
          </div>
        </div>
        <picture>
          <source srcSet="/photo/drone.webp" type="image/webp" />
          <img
            src="/photo/drone.jpg"
            alt="CreateFPV drone parçaları ve montaj"
            width="1264"
            height="802"
            fetchpriority="high"
            className="mx-auto mt-6 w-full max-w-4xl px-4 object-contain mix-blend-multiply"
          />
        </picture>
      </section>

      <section className="bg-[#f5f5f7] py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Kategoriye göre keşfet.</h2>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {cats.map((c) => (
              <Link key={c.slug} to={`/ilanlar?category=${c.slug}`} data-testid={`home-category-${c.slug}`}
                className="lift flex items-center justify-between rounded-2xl bg-white px-5 py-5 text-[15px] font-medium text-zinc-900">
                {c.name}<ArrowRight className="h-4 w-4 text-zinc-400" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-4 px-4 py-20 sm:px-6 md:grid-cols-3">
        {FEATURES.map((f) => (
          <Link key={f.title} to={f.to} className="lift rounded-3xl bg-[#f5f5f7] p-8" data-testid={`home-feature-${f.to.slice(1)}`}>
            <f.icon className="h-7 w-7 text-[#0071e3]" strokeWidth={1.5} />
            <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight">{f.title}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-zinc-500">{f.text}</p>
            <span className="mt-6 inline-flex items-center gap-1 text-[15px] text-[#0071e3]">Daha fazla <ArrowRight className="h-4 w-4" /></span>
          </Link>
        ))}
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6">
        <div className="flex items-end justify-between">
          <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Son eklenen ilanlar.</h2>
          <Link to="/ilanlar" className="text-[15px] text-[#0071e3] hover:underline" data-testid="home-all-listings">Tümünü gör ›</Link>
        </div>
        <div className="mt-8">
          {isLoading ? <Loading /> : data?.items?.length ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{data.items.map((l) => <ListingCard key={l.id} item={l} />)}</div>
          ) : <Empty title="Henüz ilan yok" hint="İlk ilanı siz verin"><Button asChild className="bg-[#0071e3] text-white"><Link to="/ilan-ver">İlan ver</Link></Button></Empty>}
        </div>
      </section>
    </>
  );
}
